import { useState, useCallback, useMemo } from "react";
import type { ChangeEvent } from "react";
import { Box, Button, Card, CardMedia, Divider, Typography } from "@mui/material";
import ImageInput from "./ImageInput";
import type { RootState } from "../store";
import { useSelector } from "react-redux";
import Theme from "../utils/theme";
import { PulseLoader } from "react-spinners";

interface IImageUploadProps {
    type: "Post" | "Avatar" | "Recipe";
    handleImageAdd: (previewUrl: string | null, data: { file: File | null; caption?: string }) => void;
    hideCaption?: boolean;
    buttonLabel?: string;
    disabledGenerate?: boolean;
    setDisabledGenerate?: (b: boolean) => void;
    parentPreviewUrl?: string | null;
    setParentPreviewUrl?: (s: string) => void;
    parentPreviewFile?: File | null;
    setParentPreviewFile?: (f: File) => void;
    loading?: boolean;
}

const ImageUpload: React.FC<IImageUploadProps> = ({
    type,
    handleImageAdd,
    hideCaption = false,
    buttonLabel = "Continue",
    disabledGenerate = false,
    setDisabledGenerate,
    parentPreviewUrl,
    setParentPreviewUrl,
    parentPreviewFile,
    setParentPreviewFile,
    loading,
}) => {
    const themeState = useSelector((state: RootState) => state.theme);
    const theme = useMemo(() => Theme(themeState.mode), [themeState.mode]);

    const [previewUrl, setPreviewUrl] = useState<string | null>(parentPreviewUrl ?? null);
    const [previewFile, setPreviewFile] = useState<File | null>(parentPreviewFile ?? null);
    const [caption, setCaption] = useState("");

    const handleImageParams = type === "Post" ? { file: previewFile, caption: caption } : { file: previewFile };

    const handleFileChange = useCallback(
        (e: ChangeEvent<HTMLInputElement>) => {
            const selected = e.target.files?.[0];
            if (selected) {
                setPreviewFile(selected);
                setPreviewUrl(URL.createObjectURL(selected));

                if (setDisabledGenerate) setDisabledGenerate(false);
                if (setParentPreviewFile) setParentPreviewFile(selected);
                if (setParentPreviewUrl) setParentPreviewUrl(URL.createObjectURL(selected));
            }
        },
        [setDisabledGenerate, setParentPreviewFile, setParentPreviewUrl]
    );

    return (
        <Box sx={{ maxWidth: 400, margin: "auto", p: 1 }}>
            <input accept="image/*" type="file" id="upload-input" style={{ display: "none" }} onChange={handleFileChange} />
            <label htmlFor="upload-input">
                <Button variant="contained" component="span" fullWidth sx={{ color: "white", backgroundColor: theme.palette.primary.main }}>
                    Select Image
                </Button>
            </label>

            {previewUrl && (
                <Card sx={{ mt: 2 }}>
                    <CardMedia onChange={() => (disabledGenerate = false)} component="img" height="200" image={previewUrl} alt="Preview" />

                    <Divider />

                    {type === "Post" && !hideCaption && <ImageInput value={caption} onChange={setCaption} />}

                    <Divider />
                    <Button
                        variant="outlined"
                        onClick={() => handleImageAdd(previewUrl, handleImageParams)}
                        fullWidth
                        disabled={disabledGenerate || loading}
                        sx={{ mt: 1, borderColor: theme.palette.primary.main, color: theme.palette.primary.main }}
                    >
                        {loading ? (
                            <Box
                                sx={{
                                    width: "100%",
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    flexDirection: "column",
                                    height: "24px",
                                }}
                            >
                                <PulseLoader size={"9px"} color={theme.palette.primary.main} />
                            </Box>
                        ) : (
                            <Typography>{buttonLabel}</Typography>
                        )}
                    </Button>
                </Card>
            )}
        </Box>
    );
};

export default ImageUpload;
